import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import {
    Box, Card, CardContent, Typography, Grid, TextField, Button, Select, MenuItem,
    FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Chip, Divider, Alert, CircularProgress, IconButton, Tooltip, Tabs, Tab
} from '@mui/material';
import { Save, Edit, Visibility, LocalShipping, Email } from '@mui/icons-material';

function EditO() {
    const { ID } = useParams();
    const navigate = useNavigate();
    const { profile } = useContext(AuthContext);
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Parse products JSON
    const parseProducts = (productsStr) => {
        try {
            if (typeof productsStr === 'string') return JSON.parse(productsStr);
            if (Array.isArray(productsStr)) return productsStr;
            return [];
        } catch { return []; }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setOrder((prev) => ({ ...prev, [name]: value }));
    };

    const handleStatusChange = async (newStatus) => {
        const oldStatus = order.status;
        setOrder(prev => ({ ...prev, status: newStatus }));

        // If changing to Shipped or Delivered, send notification email
        if ((newStatus === '3' || newStatus === '4') && oldStatus !== newStatus) {
            try {
                await fetch('https://api.elnaf.fi/api/editOrder.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...order,
                        status: newStatus,
                        username: profile?.username || '',
                        sendEmail: true
                    })
                });
                setSuccess('Status updated and confirmation email sent to customer.');
            } catch (err) {
                console.error('Status update error:', err);
            }
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const payload = {
                ...order,
                username: profile?.username || ''
            };

            const response = await fetch('https://api.elnaf.fi/api/editOrder.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();

            if (result.status === 'success') {
                setSuccess('Order updated successfully!');
                setEditMode(false);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError('Error: ' + result.message);
            }
        } catch (err) {
            console.error('Error updating order:', err);
            setError('Failed to save order. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const response = await fetch(`https://api.elnaf.fi/api/orders.php?id=${ID}`);
                const data = await response.json();
                if (data.result === 1 && data.orders?.length > 0) {
                    setOrder({ ...data.orders[0], username: profile?.username || '' });
                } else {
                    navigate('/notFound');
                }
            } catch (err) {
                setError('Failed to load order.');
            } finally {
                setLoading(false);
            }
        };
        fetchOrderDetails();
    }, [ID]);

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
        </Box>
    );

    if (!order) return null;

    const products = parseProducts(order.products);
    const statusColors = { '0': 'info', '1': 'warning', '2': 'warning', '3': 'primary', '4': 'success', '9': 'error' };
    const statusLabels = { '0': 'Order Placed', '1': 'Processing', '2': 'Packing', '3': 'Shipped', '4': 'Delivered', '9': 'Cancelled' };
    const currentStatus = String(order.status ?? 0);

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        Order #{ID}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {order.order_date || 'N/A'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                        label={statusLabels[currentStatus] || 'Pending'}
                        color={statusColors[currentStatus] || 'warning'}
                        size="medium"
                        sx={{ fontWeight: 'bold', fontSize: '0.85rem', px: 1 }}
                    />
                    <Tooltip title={editMode ? 'View Mode' : 'Edit Mode'}>
                        <IconButton onClick={() => setEditMode(!editMode)} color="primary">
                            {editMode ? <Visibility /> : <Edit />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Tabs */}
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Order Details" />
                <Tab label={`Products (${products.length})`} />
            </Tabs>

            {/* Tab 0: Order Details */}
            {activeTab === 0 && (
                <Grid container spacing={3}>
                    {/* Customer Info */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Customer Information</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="First Name" name="name" value={order.name || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Last Name" name="last_name" value={order.last_name || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Company" name="company" value={order.company || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="VAT ID" name="vat_id" value={order.vat_id || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Phone" name="phone" value={order.phone || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Email" name="email" value={order.email || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Shipping Info */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    <LocalShipping sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                                    Shipping Information
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Country/Region" name="country_region" value={order.country_region || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Town/City" name="town_city" value={order.town_city || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Street Address" name="street_address" value={order.street_address || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Shipping Address" name="shipping_address" value={order.shipping_address || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small"
                                            helperText="Different delivery address" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Post Code" name="post_code" value={order.post_code || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Order Info */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Order Settings</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Status</InputLabel>
                                            <Select name="status" value={currentStatus}
                                                onChange={(e) => {
                                                    handleInputChange(e);
                                                    if (!editMode) handleStatusChange(e.target.value);
                                                }}
                                                label="Status">
                                                <MenuItem value="0">📋 Order Placed</MenuItem>
                                                <MenuItem value="1">⚙️ Processing</MenuItem>
                                                <MenuItem value="2">📦 Packing</MenuItem>
                                                <MenuItem value="3">🚚 Shipped</MenuItem>
                                                <MenuItem value="4">✅ Delivered</MenuItem>
                                                <MenuItem value="9">❌ Cancelled</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Total (€)" name="total" value={order.total || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Shipping Method" name="shipping" value={order.shipping || ''}
                                            disabled size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth label="Shipping Cost (€)" name="shippingPay" value={order.shippingPay || ''}
                                            disabled size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Tracking Number" name="tracking_number"
                                            value={order.tracking_number || ''}
                                            onChange={handleInputChange} disabled={!editMode} size="small"
                                            placeholder="Enter tracking number"
                                            helperText="Courier tracking ID for this shipment" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Notes */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Notes</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <TextField fullWidth multiline rows={5} label="Order Notes" name="notes"
                                    value={order.notes || ''} onChange={handleInputChange}
                                    disabled={!editMode} size="small" />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Save Button */}
                    {editMode && (
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button variant="outlined" onClick={() => setEditMode(false)}>Cancel</Button>
                                <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <Save />}
                                    onClick={handleSave} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Order'}
                                </Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Tab 1: Products */}
            {activeTab === 1 && (
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Ordered Products ({products.length})
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {products.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Brand</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {products.map((p, i) => {
                                            const price = parseFloat(p.price || p.Price || 0);
                                            const qty = parseInt(p.quantity || 1);
                                            return (
                                                <TableRow key={i} hover>
                                                    <TableCell>{i + 1}</TableCell>
                                                    <TableCell>
                                                        {(p.Foto || p.image) ? (
                                                            <img src={p.Foto || p.image} alt=""
                                                                style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }} />
                                                        ) : (
                                                            <Box sx={{ width: 40, height: 40, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'grey.400' }}>N/A</Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {p.name || p.DESCRIPTION_EN || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                            {p.SKU || p.sku || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{p.MARKA || p.brand || '-'}</TableCell>
                                                    <TableCell align="center">{qty}</TableCell>
                                                    <TableCell align="right">€{price.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>€{(price * qty).toFixed(2)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {/* Totals */}
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell colSpan={5} />
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                {products.reduce((s, p) => s + parseInt(p.quantity || 1), 0)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal:</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                €{products.reduce((s, p) => s + (parseFloat(p.price || p.Price || 0) * parseInt(p.quantity || 1)), 0).toFixed(2)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info">No products found in this order.</Alert>
                        )}

                        {/* Financial Summary */}
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Grid container spacing={1}>
                                <Grid item xs={6}><Typography variant="body2" color="text.secondary">Shipping ({order.shipping || 'N/A'}):</Typography></Grid>
                                <Grid item xs={6}><Typography variant="body2" align="right">€{parseFloat(order.shippingPay || 0).toFixed(2)}</Typography></Grid>
                                <Grid item xs={6}><Typography variant="body1" fontWeight="bold">Total:</Typography></Grid>
                                <Grid item xs={6}><Typography variant="body1" fontWeight="bold" align="right" color="primary">€{parseFloat(order.total || 0).toFixed(2)}</Typography></Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}

export default EditO;